// src/components/business/MerchantRevenuePanel.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { FaChartLine, FaCoins, FaFileCsv, FaInfoCircle, FaPercentage, FaShieldAlt, FaSpinner } from 'react-icons/fa';
import { fetchMerchantRevenueDashboard, formatMoneyCents, type CommissionEvent, type MerchantRevenueSummary } from '../../lib/commissions';

type Props = { business: any };

function downloadCsv(events: CommissionEvent[], businessName: string) {
  const headers = ['date', 'type', 'source_id', 'gross', 'commission_rate', 'commission', 'net', 'status'];
  const rows = events.map((event) => [
    event.created_at,
    event.source_type,
    event.source_id || '',
    (event.gross_amount_cents / 100).toFixed(2),
    event.commission_rate.toString(),
    (event.commission_amount_cents / 100).toFixed(2),
    (event.net_amount_cents / 100).toFixed(2),
    event.status,
  ]);
  const csv = [headers, ...rows].map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `revenus-commissions-${businessName || 'forfeo'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function MerchantRevenuePanel({ business }: Props) {
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<MerchantRevenueSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!business?.id) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchMerchantRevenueDashboard(business.id, days);
        if (!cancelled) setSummary(data);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Impossible de charger les revenus.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [business?.id, days]);

  const comboRate = useMemo(() => {
    const setting = summary?.settings?.find((item) => item.product_type === 'combo');
    return setting?.commission_rate ?? 8;
  }, [summary]);

  if (loading) {
    return <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-600"><FaSpinner className="mx-auto mb-3 animate-spin text-3xl text-emerald-600" />Chargement des revenus...</div>;
  }

  if (error) {
    return <div className="rounded-3xl border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>;
  }

  const totals = summary?.totals || { gross_amount_cents: 0, commission_amount_cents: 0, net_amount_cents: 0, estimated_count: 0, captured_count: 0 };
  const events = summary?.events || [];

  return (
    <section className="space-y-6">
      <div className="overflow-hidden rounded-[2rem] bg-gradient-to-br from-white via-emerald-950 to-teal-900 p-6 text-white shadow-2xl">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.3em] text-emerald-200">Forfeo revenus</p>
            <h2 className="mt-2 text-3xl font-black">Revenus & commissions</h2>
            <p className="mt-2 max-w-2xl text-sm text-emerald-50/80">Suivez les ventes estimées, les commissions Forfeo et le montant net associé à vos combos, forfaits et offres.</p>
          </div>
          <div className="flex gap-2">
            {[30, 90, 365].map((value) => (
              <button key={value} onClick={() => setDays(value)} className={`rounded-full px-4 py-2 text-sm font-black ${days === value ? 'bg-white text-slate-950' : 'bg-white/10 text-slate-900 hover:bg-white/20'}`}>{value} j</button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"><FaChartLine className="mb-3 text-2xl text-emerald-600" /><p className="text-xs font-bold uppercase text-slate-700">Ventes brutes</p><p className="text-2xl font-black text-slate-950">{formatMoneyCents(totals.gross_amount_cents)}</p></div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"><FaPercentage className="mb-3 text-2xl text-blue-600" /><p className="text-xs font-bold uppercase text-slate-700">Commission Forfeo</p><p className="text-2xl font-black text-slate-950">{formatMoneyCents(totals.commission_amount_cents)}</p></div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"><FaCoins className="mb-3 text-2xl text-amber-600" /><p className="text-xs font-bold uppercase text-slate-700">Net estimé</p><p className="text-2xl font-black text-slate-950">{formatMoneyCents(totals.net_amount_cents)}</p></div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"><FaShieldAlt className="mb-3 text-2xl text-purple-600" /><p className="text-xs font-bold uppercase text-slate-700">Taux Combo</p><p className="text-2xl font-black text-slate-950">{comboRate}%</p></div>
      </div>

      <div className="rounded-3xl border border-blue-100 bg-blue-50 p-5 text-sm text-blue-900">
        <div className="flex gap-3"><FaInfoCircle className="mt-1 shrink-0" /><p><strong>Note juridique :</strong> les montants sont affichés à titre opérationnel. Forfeo agit comme plateforme technologique et calcule la commission applicable selon les paramètres en vigueur au moment de la vente ou de l’acceptation marchande.</p></div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h3 className="text-xl font-black text-slate-950">Activité récente</h3>
          <button onClick={() => downloadCsv(events, business?.name)} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-black text-slate-900"><FaFileCsv /> Export CSV</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead><tr className="border-b text-xs uppercase tracking-wide text-slate-700"><th className="py-3">Date</th><th>Type</th><th>Vente</th><th>Commission</th><th>Net</th><th>Statut</th></tr></thead>
            <tbody>
              {events.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-700">Aucun événement de commission pour cette période.</td></tr>}
              {events.map((event) => (
                <tr key={event.id} className="border-b last:border-0"><td className="py-3 text-slate-600">{new Date(event.created_at).toLocaleString('fr-CA')}</td><td className="font-bold text-slate-900">{event.source_type}</td><td>{formatMoneyCents(event.gross_amount_cents)}</td><td>{event.commission_rate}% · {formatMoneyCents(event.commission_amount_cents)}</td><td className="font-black text-emerald-700">{formatMoneyCents(event.net_amount_cents)}</td><td><span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">{event.status}</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
